import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import {
    CreateUserRequest,
    CreateUserResponse,
    DepartmentResponse,
    RoleResponse,
    Tab,
    UserProfile,
    UserProfileResponse,
} from '@lib/interfaces/user-management.interface';
import { Observable, map } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class UserManagementService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = environment.API_URL;

    /**
     * Get all roles from the API
     * @returns Observable of Tab array with label and value properties
     */
    getRoles(): Observable<Tab[]> {
        return this.http.get<RoleResponse>(`${this.baseUrl}/role`).pipe(
            map((response) => {
                // Map API response to Tab interface with proper labels
                return response.data.map((role) => ({
                    value: role.id,
                    label: role.name,
                }));
            }),
        );
    }

    /**
     * Get all departments from the API
     * @returns Observable of Tab array with label and value properties
     */
    getDepartments(): Observable<{ name: string; id: string }[]> {
        return this.http.get<DepartmentResponse>(`${this.baseUrl}/department`).pipe(
            map((response) => {
                // Map API response to Tab interface with proper labels
                return response.data.map((dept) => ({
                    name: dept.name,
                    id: dept.id,
                }));
            }),
        );
    }

    /**
     * Get users by role
     * @param role The role to filter users by
     * @returns Observable of UserProfile array
     */
    getUsersByRole(
        role: string,
        page: number = 0,
        size: number = 10,
        search: string = '',
    ): Observable<UserProfileResponse> {
        return this.http.get<UserProfileResponse>(
            `${this.baseUrl}/user?role=${role}&page=${page}&size=${size}&searchParam=${search}&excludeMisc=false`,
        );
    }

    /**
     * Get user details by ID
     * @param userId The ID of the user to get
     * @returns Observable of UserProfile
     */
    getUserById(userId: string): Observable<UserProfile> {
        return this.http
            .get<{ data: UserProfile; errors: any }>(`${this.baseUrl}/user/${userId}`)
            .pipe(map((response) => response.data));
    }

    /**
     * Update an existing user
     * @param userId The ID of the user to update
     * @param userData The updated user data
     * @returns Observable of the API response
     */
    updateUser(userId: string, userData: Partial<CreateUserRequest>): Observable<any> {
        return this.http.put<any>(`${this.baseUrl}/auth/faculty/${userId}`, userData);
    }

    /**
     * Delete a user
     * @param userId The ID of the user to delete
     * @returns Observable of the API response
     */
    deleteUser(userId: string): Observable<any> {
        return this.http.delete<any>(`${this.baseUrl}/user/${userId}`);
    }

    /**
     * Create a new user
     * @param userData The user data to create
     * @returns Observable of the API response
     */
    createUser(userData: CreateUserRequest): Observable<CreateUserResponse> {
        return this.http.post<CreateUserResponse>(`${this.baseUrl}/auth/faculty`, userData);
    }

    /**
     * Format role name to a more readable label
     * @param roleName The role name from the API (e.g., "FACULTY")
     * @returns Formatted label (e.g., "Faculty")
     */
    private formatRoleLabel(roleName: string): string {
        // Handle empty or null values
        if (!roleName) return '';

        // Convert to lowercase first
        const lowercase = roleName.toLowerCase();

        // Capitalize first letter
        return lowercase.charAt(0).toUpperCase() + lowercase.slice(1);
    }

    /**
     * Format department name to a more readable label
     * @param deptName The department name from the API (e.g., "CSE")
     * @returns Formatted label (e.g., "Computer Science Engineering")
     */
    private formatDepartmentLabel(deptName: string): string {
        // Handle empty or null values
        if (!deptName) return '';

        // Department name mapping for common abbreviations
        const departmentMap: Record<string, string> = {
            CSE: 'Computer Science Engineering',
            ECE: 'Electronics & Communication Engineering',
            EE: 'Electrical Engineering',
            ME: 'Mechanical Engineering',
            CE: 'Civil Engineering',
            IT: 'Information Technology',
            BT: 'Biotechnology',
            CH: 'Chemical Engineering',
        };

        // Return mapped name if available, otherwise return the original name
        return departmentMap[deptName] || deptName;
    }
}
